import { useCallback, useMemo, useState } from 'react'

import { InjectionTokens } from '@/controller/tokens'

import { ADHP, CoordWaypoint, Navaid } from '@/domain/models'
import { MapPosition } from '@/domain/protocols/MapPosition'
import { MapState, MapStateProps } from '@/domain/states/MapState'
import { RouteState, RouteStateProps } from '@/domain/states/RouteState'
import { DeleteWaypointFromRouteUseCase } from '@/domain/useCases/Route'

import { makeNavaidFeatureInfoData } from '../components/MapNavaid/navaidFormatters'
import { MapRoute } from '../components/MapRoute'
import { WaypointInfoBox, WaypointPopupInfo } from '../components/MapRoute/WaypointInfo/WaypointInfoBox'
import { FeatureInfoStateMutator } from '../states/FeatureInfoState'
import { MeasuringSegmentState, MeasuringSegmentStateProps } from '../states/MapMeasuringSegmentState'
import { RouteWaypointState, RouteWaypointStateProps } from '../states/RouteWaypointState'
import { ScreenElementsStateMutator } from '../states/ScreenElementsState'

import { MetricsEvents } from '@/constants/metricsEvents'
import { useBehaviorSubject } from '@/presentation/hooks/useBehaviorSubject'
import { useGlobalState } from '@/presentation/hooks/useGlobalState'
import { useI18n } from '@/presentation/hooks/useI18n'
import { getAdhpInfoPath } from '@/presentation/modules/AdhpInfo/utils/adhpInfoNavigation'
import {
  getCreateUserWaypointPath,
  getUserWaypointReturnState,
  isGenericCoordWaypoint
} from '@/presentation/modules/UserWaypointsScreen/utils/userWaypointNavigation'
import { openDrawer } from '@/presentation/router/DrawerRouter'
import { sendMetrics } from '@/presentation/utils/sendMetrics'

function MapRoutePresenter() {
  const { t } = useI18n()

  const [routeState] = useGlobalState<RouteState, RouteStateProps>(InjectionTokens.RouteState)
  const [mapState] = useGlobalState<MapState, MapStateProps>(InjectionTokens.MapState)
  const routeWaypointState = useBehaviorSubject<RouteWaypointStateProps>(RouteWaypointState)
  const measuringSegmentState = useBehaviorSubject<MeasuringSegmentStateProps>(MeasuringSegmentState)
  const [focusedWaypointInfo, setFocusedWaypointInfo] = useState<WaypointPopupInfo | null>(null)
  const [waypointInfoOnMapPos, setWaypointInfoOnMapPos] = useState<MapPosition>({
    position: { x: 0, y: 0 },
    coordinates: null
  })
  const [menuPosition, setMenuPosition] = useState<
    | 'top-left'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-right'
    | 'center-left'
    | 'center-right'
    | 'center-bottom'
    | 'center-top'
    | 'default'
  >('top-left')

  const waypoints = useMemo(() => routeState.activeRoute?.waypoints ?? [], [routeState.activeRoute?.waypoints])
  const highlightedWaypoint = useMemo(
    () =>
      routeWaypointState.highlightedWaypoint
        ? waypoints.find((wpt) => routeWaypointState.highlightedWaypoint.toString() === wpt.id.toString())
        : null,
    [routeWaypointState.highlightedWaypoint, waypoints]
  )
  const focusedWaypoint = useMemo(
    () =>
      focusedWaypointInfo ? (waypoints.find((wpt) => wpt.id.toString() === focusedWaypointInfo.id) ?? null) : null,
    [focusedWaypointInfo, waypoints]
  )

  const waypointInfoMenuPosition = useMemo(() => {
    const { innerWidth: width, innerHeight: height } = window

    const margin = 200
    const menuWidth = 300

    const screenRightLimit = width - margin - menuWidth
    const screenLeftLimit = margin
    const screenBottomLimit = height - margin
    const screenTopLimit = margin

    if (waypointInfoOnMapPos.position.y > screenBottomLimit) {
      if (waypointInfoOnMapPos.position.x < screenLeftLimit) {
        setMenuPosition('bottom-left')

        return {
          top: waypointInfoOnMapPos.position.y,
          left: waypointInfoOnMapPos.position.x,
          translateX: '10%',
          translateY: '-110%'
        }
      }

      if (waypointInfoOnMapPos.position.x > screenRightLimit) {
        setMenuPosition('bottom-right')

        return {
          top: waypointInfoOnMapPos.position.y,
          left: waypointInfoOnMapPos.position.x,
          translateX: '-110%',
          translateY: '-110%'
        }
      }

      setMenuPosition('center-bottom')

      return {
        top: waypointInfoOnMapPos.position.y,
        left: waypointInfoOnMapPos.position.x,
        translateX: '-50%',
        translateY: '-110%'
      }
    }

    if (waypointInfoOnMapPos.position.y < screenTopLimit) {
      if (waypointInfoOnMapPos.position.x < screenLeftLimit) {
        setMenuPosition('top-left')

        return {
          top: waypointInfoOnMapPos.position.y,
          left: waypointInfoOnMapPos.position.x,
          translateX: '10%',
          translateY: '10%'
        }
      }

      if (waypointInfoOnMapPos.position.x > screenRightLimit) {
        setMenuPosition('top-right')

        return {
          top: waypointInfoOnMapPos.position.y,
          left: waypointInfoOnMapPos.position.x,
          translateX: '-110%',
          translateY: '10%'
        }
      }

      setMenuPosition('center-top')

      return {
        top: waypointInfoOnMapPos.position.y,
        left: waypointInfoOnMapPos.position.x,
        translateX: '-50%',
        translateY: '10%'
      }
    }

    if (waypointInfoOnMapPos.position.x > screenRightLimit) {
      setMenuPosition('center-right')

      return {
        top: waypointInfoOnMapPos.position.y,
        left: waypointInfoOnMapPos.position.x,
        translateX: '-110%',
        translateY: '-50%'
      }
    }

    if (waypointInfoOnMapPos.position.x < screenLeftLimit) {
      setMenuPosition('center-left')

      return {
        top: waypointInfoOnMapPos.position.y,
        left: waypointInfoOnMapPos.position.x,
        translateX: '10%',
        translateY: '-50%'
      }
    }

    setMenuPosition('default')

    return {
      top: waypointInfoOnMapPos.position.y,
      left: waypointInfoOnMapPos.position.x,
      translateX: '10%',
      translateY: '-50%'
    }
  }, [waypointInfoOnMapPos])

  const handleWaypointClick = useCallback(
    (waypointId: string, _position: MapPosition) => {
      const focusedWaypoint = waypoints.find((wpt) => wpt.id.toString() === waypointId)
      if (!focusedWaypoint) return

      const coordWaypointName = focusedWaypoint.customName
        ? focusedWaypoint.customName
        : t('WAYPOINT_TYPE_COORD-WAYPOINT')
      setFocusedWaypointInfo({
        id: waypointId,
        name: focusedWaypoint instanceof CoordWaypoint ? coordWaypointName : focusedWaypoint.getDisplayName(),
        coordinates: focusedWaypoint.coordinates,
        code: focusedWaypoint instanceof ADHP || focusedWaypoint instanceof Navaid ? focusedWaypoint.code : undefined,
        adhpType: focusedWaypoint instanceof ADHP ? focusedWaypoint.extra.type : undefined,
        navaidType: focusedWaypoint instanceof Navaid ? focusedWaypoint.extra.type : undefined
      })
      setWaypointInfoOnMapPos(_position)
    },
    [waypoints]
  )

  const handleRemoveToRoute = (waypointId: string) => {
    const waypointToRemove = waypoints.find((wpt) => wpt.id.toString() === waypointId)
    new DeleteWaypointFromRouteUseCase().execute(waypointToRemove)

    sendMetrics(MetricsEvents.MAP_ADHP_SELECTED, { extra: 'add-to-route' })
  }

  const handleCloseWaypointInfo = () => {
    setFocusedWaypointInfo(null)
    setWaypointInfoOnMapPos({ position: { x: 0, y: 0 }, coordinates: null })
  }

  const handleShowInfo = useCallback(
    (infoCode: string) => {
      // Auxílio à navegação: abre o painel lateral de informações (mesmo do clique no navaid
      // solto no mapa), em vez do drawer de ADHP.
      if (focusedWaypoint instanceof Navaid) {
        const navaidData = makeNavaidFeatureInfoData(focusedWaypoint)
        FeatureInfoStateMutator.showNavaid(`${navaidData.code} ${navaidData.navaidType}`, navaidData)
        ScreenElementsStateMutator.setFeatureInfoPanel(true)
        handleCloseWaypointInfo()
        return
      }

      openDrawer(getAdhpInfoPath({ code: infoCode, tab: 'rotaer' }))
    },
    [focusedWaypoint]
  )

  const handleCreateUserWaypoint = useCallback(() => {
    if (!focusedWaypointInfo || !focusedWaypoint || !isGenericCoordWaypoint(focusedWaypoint)) return

    openDrawer(getCreateUserWaypointPath(focusedWaypointInfo.coordinates), getUserWaypointReturnState())
  }, [focusedWaypoint, focusedWaypointInfo])

  if (!routeState.activeRoute) {
    return null
  }

  return (
    <>
      <MapRoute
        waypoints={waypoints}
        highlightedWaypoint={highlightedWaypoint}
        nextWaypointSequence={mapState.nextWaypointSequence}
        onWaypointClick={handleWaypointClick}
      />
      {!measuringSegmentState.active &&
        !measuringSegmentState.measuring &&
        focusedWaypointInfo &&
        waypointInfoOnMapPos.position.x !== 0 &&
        waypointInfoOnMapPos.position.y !== 0 && (
          <WaypointInfoBox
            pointerPosition={menuPosition}
            focusedWaypoint={focusedWaypointInfo}
            menuScreenPos={waypointInfoMenuPosition}
            onOverlayClick={handleCloseWaypointInfo}
            onRemoveWaypointClick={handleRemoveToRoute}
            onShowInfoClick={handleShowInfo}
            onCreateUserWaypointClick={handleCreateUserWaypoint}
            canCreateUserWaypoint={!!focusedWaypoint && isGenericCoordWaypoint(focusedWaypoint)}
          />
        )}
    </>
  )
}

export { MapRoutePresenter }
